/**
 * RolesListPage.jsx — Employer v1.1 Phase 0.6 Slice A.
 *
 * Pillar-grouped roles list (Draft · Active · Closing · Closed ·
 * Archived). Replaces the legacy /employer/roles surface. Read-only.
 * No write affordances in Slice A (per pre-code plan §2.1).
 *
 * `live` rows render under the Active pillar with the legacy hint
 * chip (R-24 alias).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  AlertTriangle, Briefcase, ChevronDown, ChevronUp, Loader2, Plus, RefreshCw,
} from 'lucide-react';

import { listRoles } from '@/lib/employer/rolesApi';
import RoleStatusBadge from './RoleStatusBadge';

// Pillar ordering surfaced top-to-bottom on the page.
const PILLARS = [
  { key: 'draft',    title: 'Draft',     match: (s) => s === 'draft' },
  { key: 'active',   title: 'Active',    match: (s) => s === 'active' || s === 'live' },
  { key: 'closing',  title: 'Closing',   match: (s) => s === 'closing' },
  { key: 'closed',   title: 'Closed',    match: (s) => s === 'closed' },
  { key: 'archived', title: 'Archived',  match: (s) => s === 'archived' },
];

// RC 1.62 Stabilisation Slice (Part F · 2026-02-28) — RC 1.61
// Roles List UX patch. Pillars with > VISIBLE_DEFAULT rows render
// only the first VISIBLE_DEFAULT entries; a "Show more / Show
// less" affordance toggles full visibility per pillar. Sort,
// filter, and lifecycle badges are preserved verbatim.
const VISIBLE_DEFAULT = 5;

function fmtTs(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
  });
}

export default function RolesListPage() {
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [rows,    setRows]    = useState([]);
  // RC 1.62 Stabilisation Slice (Part F) — per-pillar expand state.
  // Each pillar key maps to a boolean: true = full list visible.
  // Defaults to collapsed for every pillar.
  const [pillarExpanded, setPillarExpanded] = useState({});
  const togglePillar = useCallback((key) => {
    setPillarExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listRoles();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const byPillar = useMemo(() => {
    const out = Object.fromEntries(PILLARS.map((p) => [p.key, []]));
    for (const r of rows) {
      const s = (r.status || '').toLowerCase();
      const pillar = PILLARS.find((p) => p.match(s));
      if (pillar) out[pillar.key].push(r);
    }
    // Sort each bucket newest-first by updated_at fallback created_at.
    for (const key of Object.keys(out)) {
      out[key].sort((a, b) => {
        const ta = new Date(a.updated_at || a.created_at || 0).getTime();
        const tb = new Date(b.updated_at || b.created_at || 0).getTime();
        return tb - ta;
      });
    }
    return out;
  }, [rows]);

  const totalCount = rows.length;

  if (loading) {
    return (
      <div
        className="flex items-center gap-2 p-8 text-sm text-slate-300"
        data-testid="roles-list-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading roles…
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6" data-testid="roles-list-error">
        <Card>
          <CardContent className="flex items-start gap-3 p-6">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-rose-200" />
            <div className="space-y-2">
              <div className="font-medium text-slate-100">
                Couldn't load roles
              </div>
              <div className="text-sm text-slate-300">{error}</div>
              <Button size="sm" onClick={load}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (totalCount === 0) {
    return (
      <div className="p-6" data-testid="roles-list-empty">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              No roles yet
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-slate-300">
            <p>
              Roles describe the positions you're hiring for. Each role
              moves through a structured lifecycle: <em>draft</em> →
              <em> active</em> → <em>closing</em> → <em>closed</em> →
              <em> archived</em>. You can refine criteria at any point
              while a role is still in draft.
            </p>
            <p className="text-xs text-ndor-ink-muted">
              New to NDORFlow?{' '}
              <Link
                to="/employer/help/workflow"
                className="underline hover:text-ndor-ink"
                data-testid="roles-list-empty-workflow-link"
              >
                Read the workflow primer
              </Link>{' '}
              first — it explains how each role's evaluation framework
              shapes everything downstream.
            </p>
            <Button
              onClick={() => navigate('/employer/roles/new')}
              data-testid="roles-list-create-cta"
            >
              <Plus className="mr-2 h-4 w-4" />
              Create role
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="roles-list-page">
      <header className="flex flex-row items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ndor-ink">Roles</h1>
          <p className="text-sm text-slate-300">
            {totalCount} role{totalCount === 1 ? '' : 's'} in your account
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={load}
            data-testid="roles-list-refresh"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button
            onClick={() => navigate('/employer/roles/new')}
            data-testid="roles-list-new-role"
          >
            <Plus className="mr-2 h-4 w-4" />
            New role
          </Button>
        </div>
      </header>

      {PILLARS.map((pillar) => {
        const bucket = byPillar[pillar.key];
        if (!bucket || bucket.length === 0) return null;
        const expanded = !!pillarExpanded[pillar.key];
        const overflowing = bucket.length > VISIBLE_DEFAULT;
        const visibleRows = overflowing && !expanded
          ? bucket.slice(0, VISIBLE_DEFAULT)
          : bucket;
        const hidden = bucket.length - visibleRows.length;
        return (
          <section
            key={pillar.key}
            className="space-y-2"
            data-testid={`roles-list-pillar-${pillar.key}`}
          >
            <h2 className="text-sm font-medium uppercase tracking-wide text-slate-300">
              {pillar.title}{' '}
              <span className="text-slate-300">({bucket.length})</span>
            </h2>
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Published</TableHead>
                      <TableHead>Last updated</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleRows.map((r) => (
                      <TableRow
                        key={r.id}
                        className="cursor-pointer hover:bg-ndor-surface-alt"
                        onClick={() => navigate(`/employer/roles/${r.id}`)}
                        data-testid={`roles-list-row-${r.id}`}
                      >
                        <TableCell className="font-medium">
                          <Link
                            to={`/employer/roles/${r.id}`}
                            className="text-slate-100 hover:underline"
                          >
                            {r.title || '(untitled)'}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <RoleStatusBadge status={r.status} />
                        </TableCell>
                        <TableCell className="text-sm text-slate-300">
                          {fmtTs(r.published_at)}
                        </TableCell>
                        <TableCell className="text-sm text-slate-300">
                          {fmtTs(r.updated_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {overflowing && (
                  <div
                    className="border-t border-ndor-edge px-4 py-2 flex items-center justify-between"
                    data-testid={`roles-list-pillar-${pillar.key}-overflow`}
                  >
                    <span className="text-xs text-ndor-ink-muted">
                      {expanded
                        ? `Showing all ${bucket.length} roles in ${pillar.title}.`
                        : `Showing ${VISIBLE_DEFAULT} of ${bucket.length} · ${hidden} more in ${pillar.title}.`}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => togglePillar(pillar.key)}
                      className="text-xs"
                      data-testid={`roles-list-pillar-${pillar.key}-toggle`}
                      aria-expanded={expanded}
                    >
                      {expanded ? (
                        <>
                          <ChevronUp className="mr-1 h-3.5 w-3.5" />
                          Show less
                        </>
                      ) : (
                        <>
                          <ChevronDown className="mr-1 h-3.5 w-3.5" />
                          Show more
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </section>
        );
      })}
    </div>
  );
}
